import { useCallback, useState } from "react"
import { supabase } from "@/lib/supabase/client"
import { useAuth } from "@/hooks/useAuth"
import { useTenant } from "@/hooks/useTenant"
import { telemetry } from "@/lib/telemetry/client"
import type { FeedbackCategory, FeedbackTranscriptionSource } from "@/types/index"
import { mensagemParaUsuario } from "@/lib/erroParaUsuario"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string

export interface SubmitFeedbackPayload {
  category: FeedbackCategory
  message: string
  transcriptionSource: FeedbackTranscriptionSource
  screenshotBlob: Blob | null
}

export interface SubmitFeedbackResult {
  feedback_id: string
  reference_id: string
  /** Status do processamento. "processing" = fast path retornou e
   *  análise IA + criação ClickUp rodam em background no servidor. */
  status?: "processing" | "completed"
  /** Mensagem amigável retornada pelo servidor (ex: "Feedback recebido!"). */
  message?: string
  /** Disponível somente quando o servidor responde com todos os dados
   *  (caminho legado, antes do fast path). No fluxo atual fica indefinido. */
  task_number?: number
  ui_title?: string
  clickup_task_url?: string | null
  warning?: string
}

export interface UseSupportFeedbackReturn {
  submit: (payload: SubmitFeedbackPayload) => Promise<SubmitFeedbackResult>
  isSubmitting: boolean
  error: string | null
}

export function useSupportFeedback(): UseSupportFeedbackReturn {
  const { user } = useAuth()
  const { activeTenantId, currentTenant } = useTenant()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(
    async (payload: SubmitFeedbackPayload): Promise<SubmitFeedbackResult> => {
      setError(null)
      setIsSubmitting(true)

      try {
        const tenantId = activeTenantId ?? currentTenant?.id
        if (!user || !tenantId) {
          throw new Error("Sessão ou unidade indisponível. Recarregue a página.")
        }

        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (!session?.access_token) {
          throw new Error("Sessão expirada. Entre novamente.")
        }

        const formData = new FormData()
        formData.append("category", payload.category)
        formData.append("message", payload.message)
        formData.append("transcription_source", payload.transcriptionSource)
        formData.append("tenant_id", tenantId)
        formData.append("page_url", window.location.href)
        formData.append("page_title", document.title)
        formData.append("user_agent", navigator.userAgent)
        formData.append("viewport_w", String(window.innerWidth))
        formData.append("viewport_h", String(window.innerHeight))
        // Task #118: envia o commit SHA do build que o usuário está vendo,
        // pra que o suporte saiba exatamente qual versão tem o problema
        // (pode ser diferente do main atual por cache do browser/CDN ou
        // deploy lento). Injeção do __COMMIT_SHA__ vem do vite.config.ts.
        if (typeof __COMMIT_SHA__ === "string" && __COMMIT_SHA__) {
          formData.append("commit_sha", __COMMIT_SHA__)
        }

        if (payload.screenshotBlob) {
          formData.append("screenshot", payload.screenshotBlob, "screenshot.jpg")
        }

        const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-feedback`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        })

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}))
          throw new Error(errBody.message || errBody.error || `HTTP ${res.status}`)
        }

        const result = (await res.json()) as SubmitFeedbackResult

        // Telemetria — note que no fast path o ClickUp ainda está sendo
        // criado em background, então clickup_task_url normalmente vem null.
        // Isso NÃO indica falha; é só o estado do retorno imediato.
        telemetry.track({
          event_type: "action",
          event_name: "feedback.submit",
          properties: {
            category: payload.category,
            transcription_source: payload.transcriptionSource,
            has_screenshot: !!payload.screenshotBlob,
            message_length: payload.message.length,
            background_processing: result.status === "processing",
            clickup_synced: !!result.clickup_task_url,
          },
        })

        return result
      } catch (err) {
        setError(
          mensagemParaUsuario(
            err,
            "Não foi possível enviar sua mensagem ao suporte. Tente de novo em instantes.",
            "useSupportFeedback",
          ),
        )
        telemetry.track({
          event_type: "error",
          event_name: "feedback.submit.failed",
          properties: {
            category: payload.category,
            // Telemetria é nossa: aqui vai o texto original do erro.
            error: (err instanceof Error ? err.message : String(err)).slice(0, 200), // erro-ok: telemetria
          },
        })
        throw err
      } finally {
        setIsSubmitting(false)
      }
    },
    [user, activeTenantId, currentTenant],
  )

  return { submit, isSubmitting, error }
}
